import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@y42/environment';
import { FeatureFlagService } from '@y42/feature-flag';
import { setGitLocationHttpHeader, setGitPathHttpHeader } from '@y42/feature/git';
import { ORG_API_URL_PARAM } from '@y42/model';
import { isNotNullish } from '@y42/util/nullish';
import { Y42FeatureFlag } from '@y42/y42-feature-flag';
import { Observable, catchError, combineLatest, map, of } from 'rxjs';
import { JobStoreModel } from '../../state/job.model';

export interface TriggerTaskResponse {
  jobs: Array<{
    job_id: string;
    dbt_id?: string;
    path: string;
  }>;
}

export interface CancelJobResponse {
  error?: string;
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class JobTriggerService {
  constructor(
    private readonly http: HttpClient,
    private readonly featureFlagService: FeatureFlagService<Y42FeatureFlag>,
  ) {}

  readonly url = `${environment.url.trigger_api_new}/company/${ORG_API_URL_PARAM}`;
  readonly legacyUrl = `${environment.url.trigger_api}/company/${ORG_API_URL_PARAM}`;

  triggerJobs(isIncremental: boolean, paths: Array<JobStoreModel['path']>): Observable<TriggerTaskResponse> {
    if (this.useNewTriggerApi) {
      return this.http.post<TriggerTaskResponse>(`${this.url}/task`, {
        incremental: isIncremental,
        location: paths,
      });
    }

    return this.triggerTasks(isIncremental, paths);
  }

  private triggerTasks(isIncremental: boolean, paths: Array<JobStoreModel['path']>): Observable<TriggerTaskResponse> {
    return combineLatest(
      paths.map((path) => this.triggerTask(isIncremental, path).pipe(catchError(() => of(undefined)))),
    ).pipe(
      map((result) => {
        return {
          jobs: result.filter(isNotNullish),
        };
      }),
    );
  }

  private triggerTask(
    isIncremental: boolean,
    path: JobStoreModel['path'],
  ): Observable<TriggerTaskResponse['jobs'][number]> {
    return this.http.post<TriggerTaskResponse['jobs'][number]>(
      `${this.legacyUrl}/task`,
      {
        incremental: isIncremental,
        location: `/${path}`,
      },
      { headers: setGitPathHttpHeader(path) },
    );
  }

  cancelJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']) {
    if (this.useNewTriggerApi) {
      return this.http.delete<CancelJobResponse>(`${this.url}/jobs/${id}`, { headers: setGitLocationHttpHeader(path) });
    }
    return this.http.delete<CancelJobResponse>(`${this.url}/status/${id}`);
  }

  private get useNewTriggerApi() {
    return this.featureFlagService.get(Y42FeatureFlag.EnableNewTriggerApi);
  }
}
